import { isValidElement, useEffect, useState } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Typography } from '@mui/material';
import { CheckboxGroupOptions, CheckboxGroupVariants } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import StackedButtonOptions from 'src/components/fragments/StackedButtonOptions';

export interface SelectOptionsDrawerProps extends DrawerComponentProps {
    multiSelect?: boolean;
    keyName?: string;
    options: {
        value: string | boolean;
        label: string;
        isSelected?: boolean;
        isPrimary?: boolean;
        icon?: IconDefinition;
        href?: string;
    }[];
    description?: string | React.ReactNode;
}

function SelectOptionsDrawer(props: SelectOptionsDrawerProps) {
    const {
        multiSelect = false,
        keyName = 'value',
        onClose,
        onNext,
        options = [],
        description,
        title = 'Select option',
    } = props;

    // Only used for multi-select
    const [currentOptions, setCurrentOptions] = useState(options);

    const handleSelectOption = (selected: any) => {
        onNext({ [keyName]: selected });
    };

    // CheckboxGroupOptions keys options by string, but this drawer also serves single-select
    // callers that pass boolean values (see SelectFacilityPage, UserShiftPage).
    const checkboxOptions = currentOptions.map((option) => ({
        ...option,
        value: String(option.value),
    }));

    const selectedOptions = currentOptions.reduce<Record<string, boolean>>(
        (selectedMap, option) =>
            option.isSelected ? { ...selectedMap, [String(option.value)]: true } : selectedMap,
        {}
    );

    const handleToggleOption = (optionId: string, isChecked: boolean) => {
        setCurrentOptions(
            currentOptions.map((option) =>
                String(option.value) === optionId ? { ...option, isSelected: isChecked } : option
            )
        );
    };

    const handleMultiSelectConfirm = () => {
        const selectedValues = currentOptions
            .filter((option) => option.isSelected)
            .map((option) => option.value);
        onNext({ value: selectedValues });
    };

    useEffect(() => {
        setCurrentOptions(options);
    }, [options]);

    return (
        <>
            <DrawerTitle title={title}>
                {isValidElement(description) ? (
                    description
                ) : description ? (
                    <Typography variant="body1" sx={{ color: 'gray.900' }}>
                        {description}
                    </Typography>
                ) : null}
            </DrawerTitle>

            <DrawerBody sx={{ pb: 4, gap: 2 }}>
                {multiSelect ? (
                    <CheckboxGroupOptions
                        variant={CheckboxGroupVariants.Outlined}
                        options={checkboxOptions}
                        onSelectOption={handleToggleOption}
                        selectedOptions={selectedOptions}
                        formGroupProps={{ 'aria-label': title }}
                    />
                ) : (
                    <StackedButtonOptions
                        cancelAction={{ onSelect: onClose }}
                        onSelect={handleSelectOption}
                        options={currentOptions}
                    />
                )}
            </DrawerBody>

            {multiSelect && (
                <DrawerFooter>
                    <StackedButtonOptions
                        cancelAction={{ onSelect: onClose }}
                        onSelect={handleMultiSelectConfirm}
                        options={[{ value: '', label: 'Confirm', isPrimary: true }]}
                    />
                </DrawerFooter>
            )}
        </>
    );
}

export default SelectOptionsDrawer;
